import { normalizeSubscribedPromptAnswers } from '@api/utilities/helpers';
import {
  COMPONENT_DRAFT_CONFIG,
  DEFAULT_VISUALIZATION_TYPE,
  IGNORED_TOOL_NAMES,
  STREAMING_STATUSES,
} from './constants';

const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;

const getCallId = (m) =>
  isNonEmptyString(m?.callId) ? m.callId : isNonEmptyString(m?.call_id) ? m.call_id : null;

const getMessageType = (m) => (isNonEmptyString(m?.type) ? m.type.toLowerCase() : null);

const startCase = (value = '') =>
  String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const humanizeComponentId = (id) => startCase(String(id || '').replace(/[-_]+/g, ' '));

const extractTextFromAssistantContent = (content) => {
  if (Array.isArray(content)) {
    return content
      .map((part) =>
        isNonEmptyString(part?.text)
          ? part.text
          : isNonEmptyString(part?.content)
            ? part.content
            : ''
      )
      .filter(Boolean)
      .join('\n\n')
      .trim();
  }

  // Sometimes assistant content might be string
  if (isNonEmptyString(content)) return content.trim();

  return '';
};

const extractMessageText = (message) => {
  // user messages in response are: { content: "...", role: "user" }
  if (isNonEmptyString(message?.content)) return message.content.trim();

  // assistant messages are: { content: [...], role: "assistant", type: "message" }
  const fromAssistantParts = extractTextFromAssistantContent(message?.content);
  if (fromAssistantParts) return fromAssistantParts;

  // function_call_output messages usually use "output"
  if (isNonEmptyString(message?.output)) return message.output.trim();

  return '';
};

const normalizeRole = (message) => {
  const rawRole = isNonEmptyString(message?.role) ? message.role.toLowerCase() : null;
  if (rawRole === 'user' || rawRole === 'assistant') return rawRole;

  // Tool call "message" rows should be treated as assistant
  const type = getMessageType(message);
  if (type === 'message') return 'assistant';

  return null;
};

const resolveMessageId = (message, role, index, createFallbackId) => {
  const explicitId =
    message?.id ?? message?.message_id ?? message?.messageId ?? message?.uuid ?? null;

  if (explicitId != null) return String(explicitId);

  const callId = getCallId(message);
  if (role === 'assistant' && callId) return `assistant-call-${callId}`;

  return createFallbackId ? createFallbackId(role) : `${role}-${index}`;
};

const resolveVisualizationType = (rawType) => {
  if (!isNonEmptyString(rawType)) return DEFAULT_VISUALIZATION_TYPE;
  const trimmed = rawType.trim();
  if (!trimmed || trimmed === '-' || trimmed.toLowerCase() === 'none') {
    return DEFAULT_VISUALIZATION_TYPE;
  }
  return trimmed;
};

const normalizeFlowState = (rawFlowState) => {
  if (!rawFlowState || typeof rawFlowState !== 'object') return null;

  const topicsRaw = rawFlowState.topics || [];
  const topics = Array.isArray(topicsRaw)
    ? topicsRaw.filter(
        (topicId) =>
          topicId !== undefined &&
          topicId !== null &&
          (typeof topicId !== 'string' || topicId.trim().length > 0)
      )
    : [];

  const editablePromptAnswersRaw = rawFlowState.editablePromptAnswers;
  const editablePromptAnswersSource =
    editablePromptAnswersRaw && typeof editablePromptAnswersRaw === 'object'
      ? editablePromptAnswersRaw
      : {};

  return {
    dateRangeEnd: rawFlowState.dateRangeEnd || null,
    dateRangeStart: rawFlowState.dateRangeStart || null,
    editablePromptAnswers: normalizeSubscribedPromptAnswers(editablePromptAnswersSource),
    focusFlowId: rawFlowState.focusFlowId ?? rawFlowState.focus_flow_id ?? null,
    narrativeName: rawFlowState.narrativeName ?? '',
    readOnlyPromptAnswers: normalizeSubscribedPromptAnswers(rawFlowState.readOnlyPromptAnswers),
    topics,
  };
};

export const mapFlowStateTopicsToDrafts = (rawFlowState) => {
  const normalized = normalizeFlowState(rawFlowState);
  if (!normalized) return [];

  return normalized.topics.map((componentId) => {
    const config = COMPONENT_DRAFT_CONFIG[componentId] || {};
    const type = resolveVisualizationType(config.type);

    return {
      componentId,
      dataset: {
        type,
      },
      id: `draft-${componentId}`,
      title: config.title || humanizeComponentId(componentId),
      visualizationType: type,
    };
  });
};

export const extractFlowState = (rawFlowState) => normalizeFlowState(rawFlowState);

/**
 * @param {any} payload
 * @param {{createFallbackId?: (role: string) => string}} options
 * @return {{messages: Array<{id: string, role: string, content: string, isStreaming?: boolean}>}}
 */
export const transformChatSessionData = (payload, options = {}) => {
  const { createFallbackId } = options;

  const messages = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.messages)
      ? payload.messages
      : Array.isArray(payload?.data?.messages)
        ? payload.data.messages
        : [];

  const toolMetadataByCallId = new Map();
  messages.forEach((m) => {
    if (getMessageType(m) !== 'function_call') return;
    const callId = getCallId(m);
    if (callId) toolMetadataByCallId.set(callId, m);
  });

  const results = [];

  messages.forEach((message, index) => {
    const type = getMessageType(message);

    if (type === 'function_call') return;

    if (type === 'function_call_output') {
      const callId = getCallId(message);
      if (!callId) return;

      const toolMetadataEntry = toolMetadataByCallId.get(callId);
      if (!toolMetadataEntry) return;

      if (IGNORED_TOOL_NAMES.has(toolMetadataEntry?.name)) return;

      return;
    }

    const role = normalizeRole(message);
    if (!role) return;

    const text = extractMessageText(message);
    if (!isNonEmptyString(text)) return;

    const status = isNonEmptyString(message?.status) ? message.status.toLowerCase() : null;

    results.push({
      content: text,
      id: resolveMessageId(message, role, index, createFallbackId),
      isStreaming:
        Boolean(message?.isStreaming ?? message?.is_streaming) ||
        (status ? STREAMING_STATUSES.has(status) : false) ||
        type === 'streaming',
      role,
    });
  });

  return {
    messages: results,
  };
};

/**
 * @param {any} payload
 * @return {{flowId: number|null, flowState: Object|null}}
 */
export const transformFlowStateData = (payload) => {
  const flowState = extractFlowState(payload);

  return {
    flowId: flowState?.focusFlowId ?? null,
    flowState,
  };
};

/**
 * Maps a project API response to camelCase keys expected by the UI.
 *
 * @param {Object} [payload={}] raw API response
 * @returns {{
 *   chatSessionId: string|null,
 *   createdDatetime: string|null,
 *   flowIds: number[],
 *   id: number|null,
 *   isDeleted: boolean,
 *   isFavorite: boolean,
 *   name: string|null,
 *   narrativeCount: number,
 *   panel: string|null,
 *   updatedDatetime: string|null,
 *   userId: number|null
 * }}
 */
export const transformProject = (payload = {}) => {
  if (payload == null) {
    return {
      chatSessionId: null,
      createdDatetime: null,
      flowIds: [],
      id: null,
      isDeleted: false,
      isFavorite: false,
      name: null,
      narrativeCount: 0,
      panel: null,
      updatedDatetime: null,
      userId: null,
    };
  }

  if (typeof payload === 'number' || typeof payload === 'string') {
    return {
      chatSessionId: null,
      createdDatetime: null,
      flowIds: [],
      id: Number(payload),
      isDeleted: false,
      isFavorite: false,
      name: null,
      narrativeCount: 0,
      panel: null,
      updatedDatetime: null,
      userId: null,
    };
  }

  return {
    chatSessionId: payload.chat_session_id,
    createdDatetime: payload.created_datetime,
    flowIds: Array.isArray(payload.flow_ids) ? payload.flow_ids : [],
    id: payload.id,
    isDeleted: Boolean(payload.is_deleted),
    isFavorite: Boolean(payload.is_favorite),
    name: payload.name,
    narrativeCount: Number(payload.narrative_count) || 0,
    panel: payload.panel,
    updatedDatetime: payload.updated_datetime,
    userId: payload.user_id,
  };
};
